import { useState, useCallback } from 'react';
import type { JobSearchParams, JobType, ExperienceLevel, LocationType } from '../../types/job';
import {
  JOB_TYPE_LABELS,
  EXPERIENCE_LEVEL_LABELS,
  LOCATION_TYPE_LABELS,
  JOB_CATEGORIES,
} from '../../types/job';

interface JobFiltersProps {
  filters: JobSearchParams;
  onChange: (filters: JobSearchParams) => void;
  onSearch: () => void;
}

export function JobFilters({ filters, onChange, onSearch }: JobFiltersProps) {
  const [showAdvanced, setShowAdvanced] = useState(false);

  const updateFilter = useCallback(
    <K extends keyof JobSearchParams>(key: K, value: JobSearchParams[K]) => {
      onChange({ ...filters, [key]: value, page: 1 });
    },
    [filters, onChange]
  );

  const toggleArrayFilter = useCallback(
    <K extends keyof JobSearchParams>(
      key: K,
      value: string
    ) => {
      const currentArray = (filters[key] as string[] | undefined) || [];
      const newArray = currentArray.includes(value)
        ? currentArray.filter(v => v !== value)
        : [...currentArray, value];
      updateFilter(key, newArray.length > 0 ? (newArray as JobSearchParams[K]) : undefined);
    },
    [filters, updateFilter]
  );

  const clearFilters = () => {
    onChange({ page: 1, limit: filters.limit || 20 });
  };

  const hasActiveFilters = !!(
    filters.q ||
    filters.category ||
    filters.job_type?.length ||
    filters.experience_level?.length ||
    filters.location_type?.length ||
    filters.city ||
    filters.salary_min ||
    filters.salary_max ||
    filters.skill?.length
  );

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      {/* Search bar */}
      <div className="flex gap-4 mb-6">
        <div className="flex-1">
          <label htmlFor="search" className="sr-only">
            Search jobs
          </label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <svg
                className="h-5 w-5 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
            </div>
            <input
              type="text"
              id="search"
              className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              placeholder="Search job titles, skills, companies..."
              value={filters.q || ''}
              onChange={e => updateFilter('q', e.target.value || undefined)}
              onKeyDown={e => e.key === 'Enter' && onSearch()}
            />
          </div>
        </div>
        <button
          onClick={onSearch}
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 font-medium"
        >
          Search
        </button>
      </div>

      {/* Quick filters */}
      <div className="flex flex-wrap gap-4 mb-4">
        {/* Category */}
        <div>
          <label htmlFor="category" className="block text-sm font-medium text-gray-700 mb-1">
            Category
          </label>
          <select
            id="category"
            className="block w-full pl-3 pr-10 py-2 text-base border border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
            value={filters.category || ''}
            onChange={e => updateFilter('category', e.target.value || undefined)}
          >
            <option value="">All Categories</option>
            {JOB_CATEGORIES.map(cat => (
              <option key={cat} value={cat}>
                {cat}
              </option>
            ))}
          </select>
        </div>

        {/* Location Type */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Work Style</label>
          <div className="flex gap-2">
            {(Object.keys(LOCATION_TYPE_LABELS) as LocationType[]).map(type => (
              <button
                key={type}
                onClick={() => toggleArrayFilter('location_type', type)}
                className={`px-3 py-1.5 text-sm rounded-md border ${
                  filters.location_type?.includes(type)
                    ? 'bg-blue-50 border-blue-300 text-blue-700'
                    : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                }`}
              >
                {LOCATION_TYPE_LABELS[type]}
              </button>
            ))}
          </div>
        </div>

        {/* Sort */}
        <div className="ml-auto">
          <label htmlFor="sort" className="block text-sm font-medium text-gray-700 mb-1">
            Sort by
          </label>
          <select
            id="sort"
            className="block w-full pl-3 pr-10 py-2 text-base border border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
            value={filters.sort || 'relevance'}
            onChange={e =>
              updateFilter('sort', (e.target.value as 'relevance' | 'date' | 'salary') || undefined)
            }
          >
            <option value="relevance">Relevance</option>
            <option value="date">Most Recent</option>
            <option value="salary">Salary</option>
          </select>
        </div>
      </div>

      {/* Advanced filters toggle */}
      <button
        onClick={() => setShowAdvanced(!showAdvanced)}
        className="text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1"
      >
        {showAdvanced ? 'Hide' : 'Show'} advanced filters
        <svg
          className={`w-4 h-4 transition-transform ${showAdvanced ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {/* Advanced filters */}
      {showAdvanced && (
        <div className="mt-4 pt-4 border-t border-gray-200 space-y-4">
          {/* Job Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Job Type</label>
            <div className="flex flex-wrap gap-2">
              {(Object.keys(JOB_TYPE_LABELS) as JobType[]).map(type => (
                <label
                  key={type}
                  className="inline-flex items-center px-3 py-1.5 rounded-md border cursor-pointer hover:bg-gray-50"
                >
                  <input
                    type="checkbox"
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded mr-2"
                    checked={filters.job_type?.includes(type) || false}
                    onChange={() => toggleArrayFilter('job_type', type)}
                  />
                  <span className="text-sm text-gray-700">{JOB_TYPE_LABELS[type]}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Experience Level */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Experience Level</label>
            <div className="flex flex-wrap gap-2">
              {(Object.keys(EXPERIENCE_LEVEL_LABELS) as ExperienceLevel[]).map(level => (
                <label
                  key={level}
                  className="inline-flex items-center px-3 py-1.5 rounded-md border cursor-pointer hover:bg-gray-50"
                >
                  <input
                    type="checkbox"
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded mr-2"
                    checked={filters.experience_level?.includes(level) || false}
                    onChange={() => toggleArrayFilter('experience_level', level)}
                  />
                  <span className="text-sm text-gray-700">{EXPERIENCE_LEVEL_LABELS[level]}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Salary Range */}
          <div className="flex gap-4">
            <div className="flex-1">
              <label htmlFor="salary_min" className="block text-sm font-medium text-gray-700 mb-1">
                Min Salary (EUR)
              </label>
              <input
                type="number"
                id="salary_min"
                className="block w-full pl-3 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                placeholder="e.g., 50000"
                value={filters.salary_min || ''}
                onChange={e =>
                  updateFilter('salary_min', e.target.value ? parseInt(e.target.value) : undefined)
                }
              />
            </div>
            <div className="flex-1">
              <label htmlFor="salary_max" className="block text-sm font-medium text-gray-700 mb-1">
                Max Salary (EUR)
              </label>
              <input
                type="number"
                id="salary_max"
                className="block w-full pl-3 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                placeholder="e.g., 100000"
                value={filters.salary_max || ''}
                onChange={e =>
                  updateFilter('salary_max', e.target.value ? parseInt(e.target.value) : undefined)
                }
              />
            </div>
          </div>

          {/* City */}
          <div>
            <label htmlFor="city" className="block text-sm font-medium text-gray-700 mb-1">
              City
            </label>
            <input
              type="text"
              id="city"
              className="block w-full pl-3 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              placeholder="e.g., Dublin"
              value={filters.city || ''}
              onChange={e => updateFilter('city', e.target.value || undefined)}
            />
          </div>
        </div>
      )}

      {/* Active filters summary */}
      {hasActiveFilters && (
        <div className="mt-4 pt-4 border-t border-gray-200 flex items-center justify-between">
          <span className="text-sm text-gray-600">Filters applied</span>
          <button
            onClick={clearFilters}
            className="text-sm text-red-600 hover:text-red-800 font-medium"
          >
            Clear all
          </button>
        </div>
      )}
    </div>
  );
}
